// 9fbef606107a605d69c0edbcd8029e5d
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Image from '@tcp/core/src/components/common/atoms/Image';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import Button from '@tcp/core/src/components/common/atoms/Button';
import {
  getIconPath,
  getLocator,
  urlContainsQuery,
  getBrandInCaps,
  getNewSNJLogo,
} from '@tcp/core/src/utils';
import { ALL_BRANDS } from '@tcp/core/src/constants/brands.constants';
import {
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { LONGER_BRAND_IDS } from '@tcp/core/src/constants/brandSpecific.constants';
import HeaderLabels from '../../../labels/HeaderNew.labels';
import { BRAND_TO_UPDATE_COOKIE } from '../container/BrandTabs.constants';
import style from '../BrandTabs.style';

const brandNames = ALL_BRANDS?.map((brand) => {
  const labelKey = `lbl_brandlogo_${brand?.toLowerCase()}AltText`;
  return getLabelFromCopyOrFromTranslation(`global.accessibility.${[labelKey]}`, HeaderLabels);
});

const applyBrandClass = (logoClass) => {
  if (!logoClass) return '';
  const [brandId] = logoClass?.split('-').reverse();
  return `brand-tab-${brandId}`;
};

const trackBrandAnalyticsClick = (brandName, trackAnalyticsClick) => {
  trackAnalyticsClick(
    { nav_cd171: `header-logo-${brandName}`, customEvents: ['event82'] },
    { name: `header-logo-${brandName}` }
  );
};

const handleBrandClick = (
  e,
  logoClassName,
  cookieToken,
  active,
  defaultUrl,
  target,
  getNavigationXHR,
  trackAnalyticsClick
) => {
  e.preventDefault();
  const url = e.currentTarget.href || defaultUrl;
  const [brandId] = logoClassName?.split('-').reverse();
  const brandName = BRAND_TO_UPDATE_COOKIE[brandId];
  trackBrandAnalyticsClick(brandName, trackAnalyticsClick);
  getNavigationXHR({ brandName, cookieToken, active, url, target });
};

const getUpdatedUrl = ({ url, cookieToken }) => {
  if (urlContainsQuery(url)) {
    return `${url}&ct=${cookieToken}`;
  }
  return `${url}?ct=${cookieToken}`;
};

const getNavigateToValue = ({ active = '', cookieToken = '', url = '' }) => {
  return !active && cookieToken ? getUpdatedUrl({ url, cookieToken }) : url;
};

const isActiveCheckout = (active, isCheckout) => active || isCheckout;

const BrandImage = ({ brandId, logoClass, active, isCheckout, logoClassAlt }) => {
  const imgPath = getIconPath(logoClassAlt);
  let newImgPath;
  if (logoClass === 'header__brand-tab-snj' && !isActiveCheckout(active, isCheckout)) {
    newImgPath = getNewSNJLogo('snjMoon');
  }
  if (
    (logoClass === 'header__brand-tab--tcp' ||
      logoClass === 'header__brand-tab-pjp' ||
      logoClass === 'header__brand-tab-gymboree') &&
    active
  ) {
    newImgPath = getIconPath(logoClass);
  }

  return (
    <Image
      alt={brandId}
      className={logoClass}
      src={newImgPath || imgPath}
      data-locator={getLocator(logoClass)}
    />
  );
};

const getClassName = ({ active, logoClass }) => {
  return active
    ? `header-topnav__brand-tabs--activeTab ${applyBrandClass(logoClass)}`
    : applyBrandClass(logoClass);
};

const swapBrands = (brands, selectedBrandId) => {
  const selectedBrandIndex = brands?.findIndex(
    (brand) =>
      brand?.class === `header__brand-tab--${selectedBrandId}` ||
      brand?.class === `header__brand-tab-${selectedBrandId}`
  );
  const selectedBrand = brands[selectedBrandIndex];
  brands?.splice(selectedBrandIndex, 1);
  brands?.splice(0, 0, selectedBrand);
  return brands;
};
const getActiveBrandsCount = (brands = []) => {
  return brands?.length;
};

const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return null; // Return null or a default value if parsing fails
  }
};

const processData = (data) => {
  const brandData = data || '[]';
  return typeof brandData === 'string' ? safeJsonParse(brandData) : brandData;
};

const getAriaLabel = (brandName) => {
  if (brandName === 'GYM') {
    return renderLabel(
      getDynamicLabelValues(`dynamic.global_d.lbl_accessibility_logo_gym`),
      'Gymboree logo, Click to visit Gymboree Website'
    );
  }
  if (brandName === 'TCP') {
    return renderLabel(
      getDynamicLabelValues(`dynamic.global_d.lbl_accessibility_logo_tcp`),
      "The Children's Place logo, Click to visit The Children's Place Website"
    );
  }
  return '';
};

const BrandTabs = ({
  className,
  data,
  cookieToken,
  isCheckout,
  getNavigationXHR,
  trackAnalyticsClick,
  brandTabAnchorToButtonEnabled,
}) => {
  const brandTabData = processData(data);
  const brandId = LONGER_BRAND_IDS[getBrandInCaps()];

  const updatedData = swapBrands(brandTabData, brandId);
  const activeBrandsCount = getActiveBrandsCount(updatedData);
  return (
    <Fragment>
      <div className={className}>
        {updatedData &&
          updatedData.map((tabData, index) => {
            const { class: logoClass, target, url } = tabData || {};
            const logoClassAlt = `${logoClass}`;
            let active = false;
            if (
              logoClass === `header__brand-tab--${brandId}` ||
              logoClass === `header__brand-tab-${brandId}`
            ) {
              active = true;
            }
            const styleName = getClassName({ active, logoClass });
            const brandName = BRAND_TO_UPDATE_COOKIE[logoClass?.split('-').pop()] || 'Brand';
            const brandImage = BrandImage({
              brandId: brandNames[index],
              logoClass,
              active,
              isCheckout,
              logoClassAlt,
            });
            const ariaLabel = getAriaLabel(brandName);
            return !active ? (
              <>
                {brandTabAnchorToButtonEnabled ? (
                  <Button
                    className={styleName}
                    onClick={(e) =>
                      handleBrandClick(
                        e,
                        logoClass,
                        cookieToken,
                        active,
                        url,
                        target,
                        getNavigationXHR,
                        trackAnalyticsClick
                      )
                    }
                    key={logoClass}
                    aria-current={active}
                    url={url}
                    aria-label={ariaLabel}
                  >
                    {brandImage}
                  </Button>
                ) : (
                  <Anchor
                    className={styleName}
                    handleLinkClick={(event) =>
                      handleBrandClick(
                        event,
                        logoClass,
                        cookieToken,
                        active,
                        url,
                        target,
                        getNavigationXHR,
                        trackAnalyticsClick
                      )
                    }
                    key={logoClass}
                    noLink
                    aria-current={active}
                    url={url}
                    aria-label={ariaLabel}
                  >
                    {brandImage}
                  </Anchor>
                )}
                {index !== 0 && index !== activeBrandsCount - 1 && (
                  <span className="brand-seperator" />
                )}
              </>
            ) : (
              <>
                <Anchor
                  className={styleName}
                  to={getNavigateToValue({
                    active,
                    cookieToken,
                    url,
                  })}
                  target={target}
                  key={logoClass}
                  aria-current={active}
                  onClick={() => {
                    trackBrandAnalyticsClick(BRAND_TO_UPDATE_COOKIE[brandId], trackAnalyticsClick);
                  }}
                  aria-label={ariaLabel}
                >
                  {brandImage}
                </Anchor>
                {index !== 0 && index !== activeBrandsCount - 1 && (
                  <span className="brand-seperator" />
                )}
              </>
            );
          })}
      </div>
    </Fragment>
  );
};

BrandImage.propTypes = {
  brandId: PropTypes.string.isRequired,
  logoClass: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  isCheckout: PropTypes.bool.isRequired,
  logoClassAlt: PropTypes.string.isRequired,
};

BrandTabs.propTypes = {
  className: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]))
  ),
  cookieToken: PropTypes.string,
  isCheckout: PropTypes.bool,
  getNavigationXHR: PropTypes.func.isRequired,
};

BrandTabs.defaultProps = {
  cookieToken: null,
  isCheckout: false,
  data: [],
};

export default errorBoundary(withStyles(BrandTabs, style));
export { BrandTabs as BrandTabsVanilla };
